import React, { useState } from "react";
import PropTypes from "prop-types";
import ChartCard from "../ChartCard";
import TEMPORAL_CHART_PALETTE from "./temporalChartPalette";
import { isLessThanOne } from "../../../utils/helpers";
import { EXPORT_IGNORE_ATTRIBUTE } from "../../../hooks/useExportGraphsToPptx";

const formatPercentage = (value) => (isLessThanOne(value) ? "<1" : value);

const TemporalGenderChart = ({
    title,
    description,
    data = [],
    groupBy,
    layout,
    organizationName,
    clientClaimCount,
    errorMessage,
    selectedFilters,
}) => {
    const [view, setView] = useState(
        layout === "horizontal" ? "horizontal" : "vertical"
    );
    const isHorizontal = view === "horizontal";
    const categories = [
        ...new Set(
            data.flatMap((period) =>
                (period.values || []).map((value) => value.gender)
            )
        ),
    ];
    const comparisons = categories.map((category, index) => ({
        key: category,
        name: category,
        ...TEMPORAL_CHART_PALETTE[index % TEMPORAL_CHART_PALETTE.length],
    }));
    const rows = data.map((period) => ({
        temporalLabel: period.temporalLabel,
        ...Object.fromEntries(
            categories.map((category) => [
                category,
                period.values?.find((value) => value.gender === category)
                    ?.value ?? 0,
            ])
        ),
    }));
    const percentageAxis = {
        tickValues: [0, 25, 50, 75, 100],
        tickSize: 0,
        tickPadding: 8,
        format: (value) => `${value}%`,
    };
    const periodAxis = {
        tickSize: 0,
        tickPadding: 10,
        tickRotation:
            !isHorizontal && groupBy === "quarter" && data.length > 8 ? -35 : 0,
    };
    const commonOptions = {
        margin: {
            top: 25,
            right: 25,
            bottom: isHorizontal ? 50 : 75,
            left: isHorizontal ? 100 : 60,
        },
        axisLeft: isHorizontal ? periodAxis : percentageAxis,
        axisBottom: isHorizontal ? percentageAxis : periodAxis,
        enableGridX: isHorizontal,
        enableGridY: !isHorizontal,
        gridXValues: [0, 25, 50, 75, 100],
        gridYValues: [0, 25, 50, 75, 100],
        animate: true,
        motionConfig: { duration: 350 },
        theme: { text: { fontSize: 12, fill: "#525252" } },
    };
    const stackedOptions = {
        ...commonOptions,
        data: isHorizontal ? [...rows].reverse() : rows,
        keys: categories,
        indexBy: "temporalLabel",
        layout: view,
        groupMode: "stacked",
        minValue: 0,
        maxValue: 100,
        padding: 0.3,
        innerPadding: 0,
        colors: comparisons.map((comparison) => comparison.color),
        borderWidth: 0,
        borderColor: ({ id }) =>
            comparisons.find((comparison) => comparison.key === id)
                ?.borderColor,
        enableLabel: true,
        label: ({ value }) => (value > 0 ? `${formatPercentage(value)}%` : ""),
        labelSkipWidth: 30,
        labelSkipHeight: 20,
        labelTextColor: ({ color }) =>
            [
                TEMPORAL_CHART_PALETTE[1].color,
                TEMPORAL_CHART_PALETTE[2].color,
            ].includes(color)
                ? "#222222"
                : "#FFFFFF",
        isInteractive: true,
        tooltip: ({ id, value, indexValue }) => (
            <div className="dashboard-tooltip">
                {indexValue}: {id} {formatPercentage(value)}%
            </div>
        ),
    };
    const hasData = data.some((period) =>
        period.values?.some(
            (value) =>
                typeof value.value === "number" && Number.isFinite(value.value)
        )
    );

    return (
        <ChartCard
            title={title}
            description={description}
            accessibilityText={`${title} over time for ${organizationName}. Each period shows ${categories.join(", ")} percentages on a zero to one hundred percent scale.`}
            variant="bar"
            groupBy={groupBy}
            chartOptions={stackedOptions}
            chartMinWidth={
                isHorizontal ? undefined : Math.max(480, data.length * 56 + 85)
            }
            chartHeight={
                isHorizontal ? Math.max(400, data.length * 42 + 75) : undefined
            }
            comparisons={comparisons}
            percentageGroups={data.map((period) =>
                (period.values || []).map((value) => value.value)
            )}
            selectedCompanyYourCompanyTo={[]}
            selectedFilters={selectedFilters}
            clientClaimCount={clientClaimCount}
            errorMessage={
                errorMessage || (!hasData ? "No data available" : undefined)
            }
            viewControls={
                <div
                    className="gender-chart-view float-end d-flex gap-1"
                    role="group"
                    aria-label="Gender chart view"
                    {...{ [EXPORT_IGNORE_ATTRIBUTE]: "true" }}
                >
                    {[
                        {
                            value: "vertical",
                            label: "100% stacked columns",
                            icon: "fa-chart-column",
                        },
                        {
                            value: "horizontal",
                            label: "100% stacked horizontal bars",
                            icon: "fa-chart-bar",
                        },
                    ].map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            className="chart-grouping-toggle"
                            style={{
                                width: 36,
                                height: 36,
                                padding: 0,
                                marginLeft: 4,
                            }}
                            aria-label={option.label}
                            aria-pressed={view === option.value}
                            title={option.label}
                            onClick={() => setView(option.value)}
                        >
                            <i
                                className={`fa-solid ${option.icon}`}
                                aria-hidden="true"
                            />
                        </button>
                    ))}
                </div>
            }
        />
    );
};

TemporalGenderChart.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    data: PropTypes.array,
    groupBy: PropTypes.string,
    layout: PropTypes.oneOf(["horizontal", "vertical"]),
    organizationName: PropTypes.string,
    clientClaimCount: PropTypes.number,
    errorMessage: PropTypes.string,
    selectedFilters: PropTypes.object,
};

export default TemporalGenderChart;
